import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Edit2, Eye, EyeSlash, Profile } from 'iconsax-react';
import { useNavigate } from 'react-router-dom';
import { blackBackground, yellowMain } from '../../../../constants/color';
import { useAppSelector } from '@application/store/useStore';
import { useEffect, useState } from 'react';
import { formatDateDMY } from '@infrastructure/helpers';
import { InfoDetails } from '@ui/components/common/typography/InforDetail';
import { profileLabelColor, profileValueColor } from './profileText';
/** Shows only the last 4 characters; the mask length does not reveal the real length. */
export const maskDocumentNumber = (value) => {
    const trimmed = value.trim();
    return trimmed.length > 4 ? `•••• ${trimmed.slice(-4)}` : '••••';
};
const MaskedDocumentNumber = ({ value, owner }) => {
    const [revealed, setRevealed] = useState(false);
    // Re-mask when another account's data is shown.
    useEffect(() => {
        setRevealed(false);
    }, [owner, value]);
    // Re-mask when the user leaves the tab/window; unmounting (leaving the page) resets it as well.
    useEffect(() => {
        const hide = () => {
            if (document.visibilityState === 'hidden')
                setRevealed(false);
        };
        document.addEventListener('visibilitychange', hide);
        window.addEventListener('pagehide', hide);
        return () => {
            document.removeEventListener('visibilitychange', hide);
            window.removeEventListener('pagehide', hide);
        };
    }, []);
    if (!value || !value.trim())
        return null;
    return (_jsxs(Box, { component: 'span', sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Box, { component: 'span', sx: { fontVariantNumeric: 'tabular-nums' }, children: revealed ? value : maskDocumentNumber(value) }), _jsx(IconButton, { size: 'small', onClick: () => setRevealed((current) => !current), "aria-label": revealed ? 'Hide document number' : 'Show document number', "aria-pressed": revealed, sx: { p: 0.5, color: '#555555', '&.Mui-focusVisible': { outline: '2px solid #141414' } }, children: revealed ? _jsx(EyeSlash, { size: 18, "aria-hidden": 'true' }) : _jsx(Eye, { size: 18, "aria-hidden": 'true' }) })] }));
};
export const PersonalInformationCard = () => {
    const { freelancer } = useAppSelector((state) => state.freelancer);
    const { countriesDropdown } = useAppSelector((state) => state.country);
    const currentUserId = useAppSelector((state) => state.auth.currentUser?.id);
    const navigate = useNavigate();
    const text = { labelColor: profileLabelColor, valueColor: profileValueColor };
    return (_jsxs(Box, { sx: { height: '100%' }, borderRadius: '0px 0px 25px 25px', overflow: 'hidden', children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    gap: 1,
                    background: blackBackground,
                    alignItems: 'center',
                    borderRadius: '25px 25px 0px 0px',
                    padding: '25px'
                }, children: [_jsx(Profile, { style: { color: yellowMain } }), _jsx(Typography, { component: 'h2', fontWeight: 500, fontSize: '20px', color: '#FFFFFF', sx: { flexGrow: 1 }, children: "Personal information" }), _jsx(Button, { size: 'small', variant: 'outlined', onClick: () => navigate('/my-profile-account-information'), "aria-label": 'Edit profile', startIcon: _jsx(Edit2, { size: 16, "aria-hidden": 'true' }), sx: {
                            color: '#FFFFFF',
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            borderRadius: '20px',
                            textTransform: 'none',
                            minHeight: 32,
                            px: 1.5,
                            '&:hover': { borderColor: '#FFFFFF', backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                            '&.Mui-focusVisible': { outline: `2px solid ${yellowMain}`, outlineOffset: 2 }
                        }, children: "Edit" })] }), _jsxs(Box, { sx: {
                    borderRadius: '0px 0px 25px 25px ',
                    background: '#FFFFFF',
                    padding: { xs: '5px 25px', sm: '10px 35px', md: '20px 55px' },
                    height: '100%'
                }, children: [_jsx(InfoDetails, { ...text, label: 'First name', value: freelancer.firstName }), _jsx(InfoDetails, { ...text, label: 'Last name', value: freelancer.lastName }), _jsx(InfoDetails, { ...text, label: 'Date of birth', value: formatDateDMY(freelancer.dateOfBirth ? new Date(freelancer.dateOfBirth) : undefined) }), _jsx(InfoDetails, { ...text, label: 'Email address', value: freelancer.email }), _jsx(InfoDetails, { ...text, label: 'Phone number', value: freelancer.phoneNumber }), _jsx(InfoDetails, { ...text, label: 'Postal code', value: freelancer.zipCode }), _jsx(InfoDetails, { ...text, label: 'Address', value: freelancer.address }), _jsx(InfoDetails, { ...text, label: 'Document number', value: _jsx(MaskedDocumentNumber, { value: freelancer.documentNumber, owner: freelancer.id || currentUserId }) }), _jsx(InfoDetails, { ...text, label: 'Country', value: countriesDropdown?.find((c) => c.value === freelancer?.countryId)?.label })] })] }));
};
